import React from "react";
import * as axios from "axios";
import {DragDropContext, Droppable, Draggable} from '@hello-pangea/dnd';
import {
  TextField,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  ListSubheader,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import RestoreIcon from "@material-ui/icons/Restore";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import EditIcon from "@material-ui/icons/Edit";
import ImageLightbox from "../../Components/ImageLightbox";
import "./styles.css";

const baseUrl = 'https://api.appetito.ro/';

// Lowercase without diacritics, so "cartofi prajiti" finds "Cartofi prăjiți".
function normalizeText(text) {
  return String(text || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function formatExtraCategoryLimits(category) {
  const min = category.min_selection || 0;
  const max = category.max_selection || 0;
  if (!min && !max) {
    return 'Nelimitat';
  }
  return `Min ${min} · Max ${max}`;
}

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// An extra's optional photo: with one (or a description), the apps show its category as
// picture cards on the product sheet (Figma 993:4338). Uploads on pick; `onChange(url)`.
class ExtraPhotoField extends React.Component {
  state = {uploading: false};

  pick = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    this.setState({uploading: true});
    try {
      const image = await readBase64(file);
      const extension = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      const response = await axios.default.post(`${baseUrl}v1/admin/extras/image`, {image, extension}, {headers});
      this.props.onChange(response.data.url);
    } catch (e) {
      alert("Eroare la încărcarea pozei.");
    } finally {
      this.setState({uploading: false});
    }
  };

  render() {
    const {value, onChange} = this.props;
    return (
      <div style={{display: 'flex', alignItems: 'center', gap: 12, marginTop: 12}}>
        {value ? (
          <img src={value} alt="" style={{width: 56, height: 56, borderRadius: 12, objectFit: 'cover'}}/>
        ) : (
          <div style={{width: 56, height: 56, borderRadius: 12, background: '#f3f0ec'}}/>
        )}
        <Button component="label" variant="outlined" size="small" disabled={this.state.uploading}>
          {this.state.uploading ? 'Se încarcă...' : value ? 'Schimbă poza' : 'Adaugă poză'}
          <input type="file" accept="image/*" hidden onChange={this.pick}/>
        </Button>
        {!!value && (
          <Button size="small" onClick={() => onChange('')} disabled={this.state.uploading}>
            Șterge
          </Button>
        )}
      </div>
    );
  }
}

function emptyProductForm() {
  return {
    name: '',
    description: '',
    image: '',
    price: '',
    has_discount: false,
    old_price: '',
    package_fee: '',
    category_id: '',
    available: true,
    sku: '',
    sgr: false,
    ingredients: '',
    ingredients_is_image: false,
    recommended: false,
  };
}

function looksLikeUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

// UI-only sort: reorders how products are displayed without touching their
// saved positions. "position" (the default) shows the real, draggable order.
function sortProducts(products, sortBy) {
  if (sortBy === 'position') {
    return products;
  }
  const sorted = [...products];
  switch (sortBy) {
    case 'name-asc':
      sorted.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ro'));
      break;
    case 'name-desc':
      sorted.sort((a, b) => (b.name || '').localeCompare(a.name || '', 'ro'));
      break;
    case 'price-asc':
      sorted.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
      break;
    case 'price-desc':
      sorted.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
      break;
    default:
      break;
  }
  return sorted;
}

export default class Menu extends React.Component {
  state = {
    lightboxSrc: null,
    restaurants: [],
    selectedRestaurantId: '',
    restaurantCategories: [],
    loadingCategories: false,
    selectedCategoryId: '',
    products: [],
    productSortBy: 'position',
    loadingProducts: false,
    selectedProductId: null,
    productExtraCategories: [],
    loadingProductExtraCategories: false,
    selectedExtraCategoryId: '',
    categoryExtras: [],
    loadingCategoryExtras: false,
    extraCategoryDialogOpen: false,
    editingExtraCategoryId: null,
    extraCategoryForm: {name: '', unlimited: false, min_selection: '', max_selection: ''},
    extraCategorySaving: false,
    addExtraDialogOpen: false,
    extraCategorySearch: '',
    restaurantExtraCategories: [],
    loadingRestaurantExtraCategories: false,
    selectedExistingExtraCategoryId: '',
    newExtraCategoryName: '',
    newExtraCategoryUnlimited: false,
    newExtraCategoryMinSelection: '',
    newExtraCategoryMaxSelection: '',
    addExtraCategorySaving: false,
    addExtraItemDialogOpen: false,
    restaurantCategoryExtras: [],
    loadingRestaurantCategoryExtras: false,
    selectedExistingExtraIds: [],
    newExtraName: '',
    newExtraPrice: '',
    newExtraAvailable: true,
    newExtraSku: '',
    newExtraSgr: false,
    newExtraImage: '',
    newExtraDescription: '',
    addExtraItemSaving: false,
    editExtraDialogOpen: false,
    editingExtraId: null,
    editExtraForm: {name: '', price: '', available: true, sku: '', sgr: false, image: '', description: ''},
    editExtraSaving: false,
    categories: [],
    includeDeleted: false,
    categoryDialogOpen: false,
    categorySaving: false,
    newCategoryName: '',
    selectedExistingCategoryId: '',
    productDialogOpen: false,
    editingProductId: null,
    editingProductDeleted: false,
    productForm: emptyProductForm(),
    productSaving: false,
  };

  componentDidMount() {
    this.getRestaurants();
  }

  getRestaurants = async () => {
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/restaurants`, {
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({restaurants: response.data || []});
  };

  getProducts = async (restaurantId, categoryId) => {
    this.setState({loadingProducts: true});
    const token = localStorage.getItem('access_token');
    const params = {};
    if (this.state.includeDeleted) {
      params.includeDeleted = true;
    }
    if (categoryId) {
      params.categoryId = categoryId;
    }
    const response = await axios.default.get(`${baseUrl}v1/admin/restaurant/${restaurantId}/products`, {
      params,
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({products: response.data || [], loadingProducts: false});
  };

  getRestaurantCategories = async (restaurantId) => {
    this.setState({loadingCategories: true});
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/restaurant/${restaurantId}/categories`, {
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({restaurantCategories: response.data || [], loadingCategories: false});
  };

  selectRestaurant = (event) => {
    const selectedRestaurantId = event.target.value;
    this.setState({
      selectedRestaurantId,
      restaurantCategories: [],
      selectedCategoryId: '',
      products: [],
      productSortBy: 'position',
      selectedProductId: null,
      productExtraCategories: [],
      selectedExtraCategoryId: '',
      categoryExtras: [],
    });
    if (selectedRestaurantId) {
      this.getRestaurantCategories(selectedRestaurantId);
    }
  };

  selectCategory = (categoryId) => {
    this.setState({
      selectedCategoryId: categoryId,
      products: [],
      productSortBy: 'position',
      selectedProductId: null,
      productExtraCategories: [],
      selectedExtraCategoryId: '',
      categoryExtras: [],
    });
    if (categoryId) {
      this.getProducts(this.state.selectedRestaurantId, categoryId);
    }
  };

  selectProduct = (product) => {
    this.setState({
      selectedProductId: product.id,
      selectedExtraCategoryId: '',
      categoryExtras: [],
    });
    this.getProductExtraCategories(this.state.selectedRestaurantId, product.id);
  };

  getProductExtraCategories = async (restaurantId, productId) => {
    this.setState({loadingProductExtraCategories: true});
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(
      `${baseUrl}v1/admin/restaurant/${restaurantId}/products/${productId}/extra-categories`,
      {headers: {Authorization: `Bearer ${token}`}},
    );
    this.setState({productExtraCategories: response.data || [], loadingProductExtraCategories: false});
  };

  selectExtraCategory = (categoryId) => {
    this.setState({selectedExtraCategoryId: categoryId, categoryExtras: []});
    this.getCategoryExtras(this.state.selectedRestaurantId, this.state.selectedProductId, categoryId);
  };

  getCategoryExtras = async (restaurantId, productId, categoryId) => {
    this.setState({loadingCategoryExtras: true});
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(
      `${baseUrl}v1/admin/restaurant/${restaurantId}/products/${productId}/extra-categories/${categoryId}/extras`,
      {headers: {Authorization: `Bearer ${token}`}},
    );
    this.setState({categoryExtras: response.data || [], loadingCategoryExtras: false});
  };

  openEditExtraCategoryDialog = (category) => {
    const unlimited = !category.min_selection && !category.max_selection;
    this.setState({
      extraCategoryDialogOpen: true,
      editingExtraCategoryId: category.id,
      extraCategoryForm: {
        name: category.name || '',
        unlimited,
        min_selection: unlimited ? '' : category.min_selection ?? '',
        max_selection: unlimited ? '' : category.max_selection ?? '',
      },
    });
  };

  closeExtraCategoryDialog = () => {
    this.setState({extraCategoryDialogOpen: false, editingExtraCategoryId: null});
  };

  updateExtraCategoryFormField = (key) => (event) => {
    this.setState({extraCategoryForm: {...this.state.extraCategoryForm, [key]: event.target.value}});
  };

  toggleExtraCategoryUnlimited = (event) => {
    const unlimited = event.target.checked;
    this.setState({
      extraCategoryForm: {
        ...this.state.extraCategoryForm,
        unlimited,
        min_selection: unlimited ? '' : this.state.extraCategoryForm.min_selection,
        max_selection: unlimited ? '' : this.state.extraCategoryForm.max_selection,
      },
    });
  };

  saveExtraCategory = async () => {
    const {editingExtraCategoryId, extraCategoryForm, selectedRestaurantId, selectedProductId} = this.state;
    const payload = {
      name: extraCategoryForm.name,
      min_selection: extraCategoryForm.unlimited ? 0 : parseInt(extraCategoryForm.min_selection, 10) || 0,
      max_selection: extraCategoryForm.unlimited ? 0 : parseInt(extraCategoryForm.max_selection, 10) || 0,
    };

    this.setState({extraCategorySaving: true});
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      await axios.default.put(`${baseUrl}v1/admin/extra-categories/${editingExtraCategoryId}`, payload, {headers});
      await this.getProductExtraCategories(selectedRestaurantId, selectedProductId);
      this.closeExtraCategoryDialog();
    } catch (e) {
      alert("Eroare la salvarea categoriei extra.");
    } finally {
      this.setState({extraCategorySaving: false});
    }
  };

  toggleIncludeDeleted = () => {
    this.setState({includeDeleted: !this.state.includeDeleted}, () => {
      const {selectedRestaurantId, selectedCategoryId} = this.state;
      if (selectedRestaurantId && selectedCategoryId) {
        this.getProducts(selectedRestaurantId, selectedCategoryId);
      }
    });
  };

  getCategories = async () => {
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/categories`, {
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({categories: response.data || []});
  };

  openCategoryDialog = () => {
    if (!this.state.categories.length) {
      this.getCategories();
    }
    this.setState({categoryDialogOpen: true, newCategoryName: '', selectedExistingCategoryId: ''});
  };

  closeCategoryDialog = () => {
    this.setState({categoryDialogOpen: false});
  };

  addExistingCategory = () => {
    const {categories, selectedExistingCategoryId, restaurantCategories, selectedRestaurantId} = this.state;
    const category = categories.find((c) => c.id === Number(selectedExistingCategoryId));
    if (!category) {
      return;
    }
    const alreadyAdded = restaurantCategories.some((c) => c.id === category.id);
    this.setState({
      restaurantCategories: alreadyAdded ? restaurantCategories : [...restaurantCategories, category],
      selectedCategoryId: category.id,
      products: [],
      categoryDialogOpen: false,
    });
    this.getProducts(selectedRestaurantId, category.id);
  };

  createCategory = async () => {
    const {newCategoryName, restaurantCategories, categories, selectedRestaurantId} = this.state;
    if (!newCategoryName.trim()) {
      return;
    }
    this.setState({categorySaving: true});
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      const response = await axios.default.post(
        `${baseUrl}v1/admin/categories`,
        {name: newCategoryName.trim()},
        {headers},
      );
      const category = response.data;
      this.setState({
        restaurantCategories: [...restaurantCategories, category],
        categories: [...categories, category],
        selectedCategoryId: category.id,
        products: [],
        categoryDialogOpen: false,
      });
      this.getProducts(selectedRestaurantId, category.id);
    } catch (e) {
      alert("Eroare la crearea categoriei.");
    } finally {
      this.setState({categorySaving: false});
    }
  };

  submitCategoryDialog = () => {
    if (this.state.newCategoryName.trim()) {
      this.createCategory();
    } else if (this.state.selectedExistingCategoryId) {
      this.addExistingCategory();
    }
  };

  openAddProductDialog = () => {
    this.setState({
      productDialogOpen: true,
      editingProductId: null,
      editingProductDeleted: false,
      productForm: {...emptyProductForm(), category_id: this.state.selectedCategoryId || ''},
    });
  };

  openEditProductDialog = (product) => {
    this.setState({
      productDialogOpen: true,
      editingProductId: product.id,
      editingProductDeleted: !!product.deleted,
      productForm: {
        name: product.name || '',
        description: product.description || '',
        image: product.image || '',
        price: product.price ?? '',
        has_discount: !!(parseFloat(product.old_price) > 0),
        old_price: product.old_price ?? '',
        package_fee: product.package_fee ?? '',
        category_id: product.category_id || '',
        available: !!product.available,
        sku: product.sku || '',
        sgr: !!product.sgr,
        ingredients: product.ingredients || '',
        ingredients_is_image: looksLikeUrl(product.ingredients),
        recommended: !!product.recommended,
      },
    });
  };

  closeProductDialog = () => {
    this.setState({productDialogOpen: false, editingProductId: null, editingProductDeleted: false});
  };

  updateProductFormField = (key) => (event) => {
    this.setState({productForm: {...this.state.productForm, [key]: event.target.value}});
  };

  toggleProductAvailable = (event) => {
    this.setState({productForm: {...this.state.productForm, available: event.target.checked}});
  };

  toggleProductSgr = (event) => {
    this.setState({productForm: {...this.state.productForm, sgr: event.target.checked}});
  };

  toggleProductHasDiscount = (event) => {
    const hasDiscount = event.target.checked;
    this.setState({
      productForm: {
        ...this.state.productForm,
        has_discount: hasDiscount,
        old_price: hasDiscount ? this.state.productForm.old_price : '',
      },
    });
  };

  uploadProductImage = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      this.setState({productForm: {...this.state.productForm, image: base64}});
    };
    reader.readAsDataURL(file);
  };

  // Switching modes clears the value: text typed for "Valori Nutriționale"
  // isn't valid image data and vice versa.
  toggleProductIngredientsIsImage = (event) => {
    this.setState({
      productForm: {
        ...this.state.productForm,
        ingredients_is_image: event.target.checked,
        ingredients: '',
      },
    });
  };

  uploadProductIngredientsImage = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      this.setState({productForm: {...this.state.productForm, ingredients: base64}});
    };
    reader.readAsDataURL(file);
  };

  productIngredientsImagePreview = () => {
    const {ingredients} = this.state.productForm;
    if (!ingredients) {
      return '';
    }
    return ingredients.startsWith('http') ? ingredients : `data:image/jpeg;base64,${ingredients}`;
  };

  saveProduct = async () => {
    const {selectedRestaurantId, editingProductId, productForm} = this.state;
    const payload = {
      name: productForm.name,
      description: productForm.description,
      image: productForm.image,
      available: productForm.available,
      price: parseFloat(productForm.price) || 0,
      old_price: parseFloat(productForm.old_price) || 0,
      package_fee: parseFloat(productForm.package_fee) || 0,
      category_id: parseInt(productForm.category_id, 10) || 0,
      sku: productForm.sku,
      sgr: productForm.sgr,
      ingredients: productForm.ingredients,
      ingredients_is_image: productForm.ingredients_is_image,
      recommended: productForm.recommended,
    };

    this.setState({productSaving: true});
    try {
      const baseHeaders = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      if (editingProductId) {
        await axios.default.put(
          `${baseUrl}v1/admin/restaurant/${selectedRestaurantId}/products/${editingProductId}`,
          payload,
          {headers: baseHeaders},
        );
      } else {
        await axios.default.post(
          `${baseUrl}v1/admin/restaurant/${selectedRestaurantId}/products`,
          payload,
          {headers: baseHeaders},
        );
      }
      await this.getProducts(selectedRestaurantId, this.state.selectedCategoryId);
      this.closeProductDialog();
    } catch (e) {
      alert("Eroare la salvarea produsului.");
    } finally {
      this.setState({productSaving: false});
    }
  };

  deleteProduct = async () => {
    const {selectedRestaurantId, editingProductId} = this.state;
    if (!window.confirm("Ștergi acest produs?")) {
      return;
    }
    this.setState({productSaving: true});
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      await axios.default.delete(
        `${baseUrl}v1/admin/restaurant/${selectedRestaurantId}/products/${editingProductId}`,
        {headers},
      );
      await this.getProducts(selectedRestaurantId, this.state.selectedCategoryId);
      this.closeProductDialog();
    } catch (e) {
      alert("Eroare la ștergerea produsului.");
    } finally {
      this.setState({productSaving: false});
    }
  };

  recoverProduct = async () => {
    const {selectedRestaurantId, editingProductId} = this.state;
    this.setState({productSaving: true});
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      await axios.default.post(
        `${baseUrl}v1/admin/restaurant/${selectedRestaurantId}/products/${editingProductId}/recover`,
        {},
        {headers},
      );
      await this.getProducts(selectedRestaurantId, this.state.selectedCategoryId);
      this.closeProductDialog();
    } catch (e) {
      alert("Eroare la recuperarea produsului.");
    } finally {
      this.setState({productSaving: false});
    }
  };

  changeProductSortBy = (event) => {
    this.setState({productSortBy: event.target.value});
  };

  onProductDragEnd = (result) => {
    const {source, destination} = result;
    if (!destination || source.index === destination.index) {
      return;
    }
    const products = Array.from(this.state.products);
    const [moved] = products.splice(source.index, 1);
    products.splice(destination.index, 0, moved);
    this.setState({products});
    this.saveProductPositions(products);
  };

  saveProductPositions = async (products) => {
    const {selectedRestaurantId} = this.state;
    const payload = products.map((product, index) => ({id: product.id, position: index}));
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      await axios.default.put(
        `${baseUrl}v1/admin/restaurant/${selectedRestaurantId}/product-positions`,
        payload,
        {headers},
      );
    } catch (e) {
      alert("Eroare la reordonarea produselor.");
    }
  };

  onExtraCategoryDragEnd = (result) => {
    const {source, destination} = result;
    if (!destination || source.index === destination.index) {
      return;
    }
    const productExtraCategories = Array.from(this.state.productExtraCategories);
    const [moved] = productExtraCategories.splice(source.index, 1);
    productExtraCategories.splice(destination.index, 0, moved);
    this.setState({productExtraCategories});
    this.saveExtraCategoryPositions(productExtraCategories);
  };

  saveExtraCategoryPositions = async (categories) => {
    const {selectedRestaurantId, selectedProductId} = this.state;
    const payload = categories.map((category, index) => ({id: category.id, position: index}));
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      await axios.default.put(
        `${baseUrl}v1/admin/restaurant/${selectedRestaurantId}/products/${selectedProductId}/extra-category-positions`,
        payload,
        {headers},
      );
    } catch (e) {
      alert("Eroare la reordonarea categoriilor extra.");
    }
  };

  onExtraDragEnd = (result) => {
    const {source, destination} = result;
    if (!destination || source.index === destination.index) {
      return;
    }
    const categoryExtras = Array.from(this.state.categoryExtras);
    const [moved] = categoryExtras.splice(source.index, 1);
    categoryExtras.splice(destination.index, 0, moved);
    this.setState({categoryExtras});
    this.saveExtraPositions(categoryExtras);
  };

  saveExtraPositions = async (extras) => {
    const {selectedRestaurantId, selectedProductId} = this.state;
    const payload = extras.map((extra, index) => ({id: extra.id, position: index}));
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      await axios.default.put(
        `${baseUrl}v1/admin/restaurant/${selectedRestaurantId}/products/${selectedProductId}/extra-positions`,
        payload,
        {headers},
      );
    } catch (e) {
      alert("Eroare la reordonarea extraselor.");
    }
  };

  getRestaurantExtraCategories = async (restaurantId) => {
    this.setState({loadingRestaurantExtraCategories: true});
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(
      `${baseUrl}v1/admin/restaurant/${restaurantId}/extra-categories`,
      {headers: {Authorization: `Bearer ${token}`}},
    );
    this.setState({restaurantExtraCategories: response.data || [], loadingRestaurantExtraCategories: false});
  };

  openAddExtraDialog = () => {
    if (!this.state.restaurantExtraCategories.length) {
      this.getRestaurantExtraCategories(this.state.selectedRestaurantId);
    }
    this.setState({
      addExtraDialogOpen: true,
      selectedExistingExtraCategoryId: '',
      newExtraCategoryName: '',
      newExtraCategoryUnlimited: false,
      newExtraCategoryMinSelection: '',
      newExtraCategoryMaxSelection: '',
    });
  };

  closeAddExtraDialog = () => {
    this.setState({addExtraDialogOpen: false});
  };

  toggleNewExtraCategoryUnlimited = (event) => {
    const unlimited = event.target.checked;
    this.setState({
      newExtraCategoryUnlimited: unlimited,
      newExtraCategoryMinSelection: unlimited ? '' : this.state.newExtraCategoryMinSelection,
      newExtraCategoryMaxSelection: unlimited ? '' : this.state.newExtraCategoryMaxSelection,
    });
  };

  // Selecting/creating a category here just makes it the active extra
  // category for the selected product, ready for extras to be added to it —
  // there's no standalone extra-item (name/price) creation yet.
  // Adds the category to this product's extra-category list purely in the
  // UI: the backend only derives that list from actual assigned extras, so
  // until an extra is added to this category, it won't survive a reload.
  selectAddedExtraCategory = (category) => {
    const {selectedRestaurantId, selectedProductId, productExtraCategories} = this.state;
    const alreadyListed = productExtraCategories.some((c) => c.id === category.id);
    this.setState({
      productExtraCategories: alreadyListed ? productExtraCategories : [...productExtraCategories, category],
      selectedExtraCategoryId: category.id,
      categoryExtras: [],
      addExtraDialogOpen: false,
    });
    if (selectedProductId) {
      this.getCategoryExtras(selectedRestaurantId, selectedProductId, category.id);
    }
  };

  useExistingExtraCategory = () => {
    const {restaurantExtraCategories, selectedExistingExtraCategoryId} = this.state;
    const category = restaurantExtraCategories.find((c) => c.id === Number(selectedExistingExtraCategoryId));
    if (!category) {
      return;
    }
    this.selectAddedExtraCategory(category);
  };

  createExtraCategoryForAdd = async () => {
    const {
      newExtraCategoryName,
      newExtraCategoryUnlimited,
      newExtraCategoryMinSelection,
      newExtraCategoryMaxSelection,
      restaurantExtraCategories,
    } = this.state;
    if (!newExtraCategoryName.trim()) {
      return;
    }
    const payload = {
      name: newExtraCategoryName.trim(),
      min_selection: newExtraCategoryUnlimited ? 0 : parseInt(newExtraCategoryMinSelection, 10) || 0,
      max_selection: newExtraCategoryUnlimited ? 0 : parseInt(newExtraCategoryMaxSelection, 10) || 0,
    };

    this.setState({addExtraCategorySaving: true});
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      const response = await axios.default.post(`${baseUrl}v1/admin/extra-categories`, payload, {headers});
      const category = response.data;
      this.setState({restaurantExtraCategories: [...restaurantExtraCategories, category]});
      this.selectAddedExtraCategory(category);
    } catch (e) {
      alert("Eroare la crearea categoriei extra.");
    } finally {
      this.setState({addExtraCategorySaving: false});
    }
  };

  submitAddExtraDialog = () => {
    if (this.state.newExtraCategoryName.trim()) {
      this.createExtraCategoryForAdd();
    } else if (this.state.selectedExistingExtraCategoryId) {
      this.useExistingExtraCategory();
    }
  };

  hasSelectedRestaurantEbriza = () => {
    const {restaurants, selectedRestaurantId} = this.state;
    const restaurant = restaurants.find((r) => r.id === selectedRestaurantId);
    return !!(restaurant && restaurant.ebriza_client_id);
  };

  getRestaurantCategoryExtras = async (restaurantId, categoryId) => {
    this.setState({loadingRestaurantCategoryExtras: true});
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(
      `${baseUrl}v1/admin/restaurant/${restaurantId}/extra-categories/${categoryId}/extras`,
      {headers: {Authorization: `Bearer ${token}`}},
    );
    this.setState({restaurantCategoryExtras: response.data || [], loadingRestaurantCategoryExtras: false});
  };

  openAddExtraItemDialog = () => {
    const {selectedRestaurantId, selectedExtraCategoryId} = this.state;
    if (!selectedExtraCategoryId) {
      return;
    }
    this.getRestaurantCategoryExtras(selectedRestaurantId, selectedExtraCategoryId);
    this.setState({
      addExtraItemDialogOpen: true,
      selectedExistingExtraIds: [],
      newExtraName: '',
      newExtraPrice: '',
      newExtraAvailable: true,
      newExtraSku: '',
      newExtraSgr: false,
      newExtraImage: '',
      newExtraDescription: '',
    });
  };

  closeAddExtraItemDialog = () => {
    this.setState({addExtraItemDialogOpen: false});
  };

  toggleNewExtraAvailable = (event) => {
    this.setState({newExtraAvailable: event.target.checked});
  };

  toggleNewExtraSgr = (event) => {
    this.setState({newExtraSgr: event.target.checked});
  };

  isExtraAlreadyAssigned = (extraId) => this.state.categoryExtras.some((e) => e.id === extraId);

  toggleExistingExtraSelection = (extraId) => {
    if (this.isExtraAlreadyAssigned(extraId)) {
      return;
    }
    const {selectedExistingExtraIds} = this.state;
    this.setState({
      selectedExistingExtraIds: selectedExistingExtraIds.includes(extraId)
        ? selectedExistingExtraIds.filter((id) => id !== extraId)
        : [...selectedExistingExtraIds, extraId],
      newExtraName: '',
    });
  };

  // Only extras not already assigned to this category are selectable at
  // all (see toggleExistingExtraSelection) — select-all only ever touches
  // those, same as clicking each of their rows individually would.
  toggleSelectAllExtras = () => {
    const {restaurantCategoryExtras, selectedExistingExtraIds} = this.state;
    const selectableIds = restaurantCategoryExtras
      .filter((extra) => !this.isExtraAlreadyAssigned(extra.id))
      .map((extra) => extra.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedExistingExtraIds.includes(id));
    this.setState({
      selectedExistingExtraIds: allSelected ? [] : selectableIds,
      newExtraName: '',
    });
  };

  refreshCategoryExtrasAndClose = () => {
    const {selectedRestaurantId, selectedProductId, selectedExtraCategoryId} = this.state;
    this.setState({addExtraItemDialogOpen: false});
    this.getCategoryExtras(selectedRestaurantId, selectedProductId, selectedExtraCategoryId);
  };

  useExistingExtra = async () => {
    const {selectedRestaurantId, selectedProductId, selectedExistingExtraIds} = this.state;
    if (!selectedExistingExtraIds.length) {
      return;
    }
    this.setState({addExtraItemSaving: true});
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      for (const extraId of selectedExistingExtraIds) {
        await axios.default.post(
          `${baseUrl}v1/admin/restaurant/${selectedRestaurantId}/products/${selectedProductId}/extras/${extraId}/attach`,
          {},
          {headers},
        );
      }
      this.refreshCategoryExtrasAndClose();
    } catch (e) {
      alert("Eroare la asocierea extraselor.");
    } finally {
      this.setState({addExtraItemSaving: false});
    }
  };

  createExtraForAdd = async () => {
    const {
      selectedRestaurantId,
      selectedProductId,
      selectedExtraCategoryId,
      newExtraName,
      newExtraPrice,
      newExtraAvailable,
      newExtraSku,
      newExtraSgr,
      newExtraImage,
      newExtraDescription,
    } = this.state;
    if (!newExtraName.trim() || !selectedExtraCategoryId) {
      return;
    }
    const payload = {
      name: newExtraName.trim(),
      price: parseFloat(newExtraPrice) || 0,
      available: newExtraAvailable,
      extra_category_id: parseInt(selectedExtraCategoryId, 10),
      image: newExtraImage,
      description: newExtraDescription.trim(),
    };
    if (this.hasSelectedRestaurantEbriza()) {
      payload.sku = newExtraSku;
      payload.sgr = newExtraSgr;
    }

    this.setState({addExtraItemSaving: true});
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      await axios.default.post(
        `${baseUrl}v1/admin/restaurant/${selectedRestaurantId}/products/${selectedProductId}/extras`,
        payload,
        {headers},
      );
      this.refreshCategoryExtrasAndClose();
    } catch (e) {
      alert("Eroare la crearea extrasului.");
    } finally {
      this.setState({addExtraItemSaving: false});
    }
  };

  submitAddExtraItemDialog = () => {
    if (this.state.newExtraName.trim()) {
      this.createExtraForAdd();
    } else if (this.state.selectedExistingExtraIds.length) {
      this.useExistingExtra();
    }
  };

  openEditExtraItemDialog = (extra) => {
    this.setState({
      editExtraDialogOpen: true,
      editingExtraId: extra.id,
      editExtraForm: {
        name: extra.name || '',
        price: extra.price ?? '',
        available: !!extra.available,
        sku: extra.sku || '',
        sgr: !!extra.sgr,
        image: extra.image || '',
        description: extra.description || '',
      },
    });
  };

  closeEditExtraItemDialog = () => {
    this.setState({editExtraDialogOpen: false, editingExtraId: null});
  };

  updateEditExtraFormField = (key) => (event) => {
    this.setState({editExtraForm: {...this.state.editExtraForm, [key]: event.target.value}});
  };

  toggleEditExtraAvailable = (event) => {
    this.setState({editExtraForm: {...this.state.editExtraForm, available: event.target.checked}});
  };

  toggleEditExtraSgr = (event) => {
    this.setState({editExtraForm: {...this.state.editExtraForm, sgr: event.target.checked}});
  };

  saveExtraItem = async () => {
    const {editingExtraId, editExtraForm} = this.state;
    const payload = {
      name: editExtraForm.name,
      price: parseFloat(editExtraForm.price) || 0,
      available: editExtraForm.available,
      image: editExtraForm.image,
      description: editExtraForm.description.trim(),
    };
    if (this.hasSelectedRestaurantEbriza()) {
      payload.sku = editExtraForm.sku;
      payload.sgr = editExtraForm.sgr;
    }

    this.setState({editExtraSaving: true});
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      await axios.default.put(`${baseUrl}v1/admin/extras/${editingExtraId}`, payload, {headers});
      this.closeEditExtraItemDialog();
      this.getCategoryExtras(this.state.selectedRestaurantId, this.state.selectedProductId, this.state.selectedExtraCategoryId);
    } catch (e) {
      alert("Eroare la salvarea extrasului.");
    } finally {
      this.setState({editExtraSaving: false});
    }
  };

  // Both of these unassign rather than delete: the extra (or every extra in
  // the category) stays intact, just no longer attached to this product.
  detachExtra = async (extra) => {
    if (!window.confirm(`Elimini "${extra.name}" de la acest produs?`)) {
      return;
    }
    const {selectedRestaurantId, selectedProductId, selectedExtraCategoryId} = this.state;
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      await axios.default.delete(
        `${baseUrl}v1/admin/restaurant/${selectedRestaurantId}/products/${selectedProductId}/extras/${extra.id}`,
        {headers},
      );
      this.getCategoryExtras(selectedRestaurantId, selectedProductId, selectedExtraCategoryId);
    } catch (e) {
      alert("Eroare la eliminarea extrasului.");
    }
  };

  detachExtraCategory = async (category, event) => {
    event.stopPropagation();
    if (!window.confirm(`Elimini categoria "${category.name}" (și extrasele ei) de la acest produs?`)) {
      return;
    }
    const {selectedRestaurantId, selectedProductId, selectedExtraCategoryId} = this.state;
    try {
      const headers = {Authorization: `Bearer ${localStorage.getItem('access_token')}`};
      await axios.default.delete(
        `${baseUrl}v1/admin/restaurant/${selectedRestaurantId}/products/${selectedProductId}/extra-categories/${category.id}`,
        {headers},
      );
      await this.getProductExtraCategories(selectedRestaurantId, selectedProductId);
      if (category.id === selectedExtraCategoryId) {
        this.setState({selectedExtraCategoryId: '', categoryExtras: []});
      }
    } catch (e) {
      alert("Eroare la eliminarea categoriei extra.");
    }
  };

  productImagePreview = () => {
    const {image} = this.state.productForm;
    if (!image) {
      return '';
    }
    return image.startsWith('http') ? image : `data:image/jpeg;base64,${image}`;
  };

  render() {
    const {
      lightboxSrc,
      restaurants,
      selectedRestaurantId,
      restaurantCategories,
      loadingCategories,
      selectedCategoryId,
      products,
      productSortBy,
      loadingProducts,
      selectedProductId,
      productExtraCategories,
      loadingProductExtraCategories,
      selectedExtraCategoryId,
      categoryExtras,
      loadingCategoryExtras,
      extraCategoryDialogOpen,
      extraCategoryForm,
      extraCategorySaving,
      addExtraDialogOpen,
      restaurantExtraCategories,
      selectedExistingExtraCategoryId,
      newExtraCategoryName,
      newExtraCategoryUnlimited,
      newExtraCategoryMinSelection,
      newExtraCategoryMaxSelection,
      addExtraCategorySaving,
      addExtraItemDialogOpen,
      restaurantCategoryExtras,
      selectedExistingExtraIds,
      newExtraName,
      newExtraPrice,
      newExtraAvailable,
      newExtraSku,
      newExtraSgr,
      addExtraItemSaving,
      editExtraDialogOpen,
      editExtraForm,
      editExtraSaving,
      categories,
      includeDeleted,
      categoryDialogOpen,
      categorySaving,
      newCategoryName,
      selectedExistingCategoryId,
      productDialogOpen,
      editingProductId,
      editingProductDeleted,
      productForm,
      productSaving,
      extraCategorySearch,
    } = this.state;

    const extraCategoryNeedle = normalizeText(extraCategorySearch.trim());
    const filteredExtraCategories = extraCategoryNeedle
      ? restaurantExtraCategories.filter((category) => normalizeText(category.name).includes(extraCategoryNeedle))
      : restaurantExtraCategories;

    const selectedRestaurant = restaurants.find((r) => r.id === selectedRestaurantId);
    const hasEbriza = !!(selectedRestaurant && selectedRestaurant.ebriza_client_id);
    const sortedProducts = sortProducts(products, productSortBy);
    const productDragDisabled = productSortBy !== 'position';

    return (
      <div className="menu-page">
        <div className="menu-toolbar">
          <div className="menu-toolbar-left">
            <TextField
              select
              value={selectedRestaurantId}
              onChange={this.selectRestaurant}
              displayEmpty
              style={{minWidth: 280}}
            >
              <MenuItem value="">Selectează Restaurant</MenuItem>
              {restaurants.map((restaurant) => (
                <MenuItem key={restaurant.id} value={restaurant.id}>
                  {restaurant.name} {restaurant.city}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={includeDeleted}
                  onChange={this.toggleIncludeDeleted}
                  color="primary"
                />
              }
              label="Arată produsele șterse"
            />
          </div>
        </div>

        {!selectedRestaurantId && (
          <div className="menu-empty-state">Selectează un restaurant pentru a vedea meniul.</div>
        )}

        {!!selectedRestaurantId && (
          <div className="menu-columns">
            <div className="menu-column">
              <div className="menu-column-title-row">
                <div className="menu-column-title">Categorii</div>
                <IconButton size="small" title="Adaugă categorie" onClick={this.openCategoryDialog}>
                  <AddIcon fontSize="small"/>
                </IconButton>
              </div>
              <div className="menu-column-body">
                {loadingCategories ? (
                  <div className="menu-empty-state">Se încarcă...</div>
                ) : restaurantCategories.length === 0 ? (
                  <div className="menu-empty-state">Nicio categorie.</div>
                ) : (
                  <div className="menu-category-list">
                    {restaurantCategories.map((category) => (
                      <div
                        className={`menu-category-row${category.id === selectedCategoryId ? ' menu-category-row-selected' : ''}`}
                        key={category.id}
                        onClick={() => this.selectCategory(category.id)}
                      >
                        {category.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="menu-column">
              <div className="menu-column-title-row">
                <div className="menu-column-title">Produse</div>
                <IconButton
                  size="small"
                  title="Adaugă produs"
                  disabled={!selectedCategoryId}
                  onClick={this.openAddProductDialog}
                >
                  <AddIcon fontSize="small"/>
                </IconButton>
              </div>
              <div className="menu-column-body">
                {!selectedCategoryId ? (
                  <div className="menu-empty-state">Selectează o categorie pentru a vedea produsele.</div>
                ) : loadingProducts ? (
                  <div className="menu-empty-state">Se încarcă...</div>
                ) : (
                  <>
                    <div className="menu-product-sort-row">
                      <TextField
                        select
                        label="Sortează"
                        value={productSortBy}
                        onChange={this.changeProductSortBy}
                        margin="dense"
                        size="small"
                        fullWidth
                      >
                        <MenuItem value="position">Ordine manuală</MenuItem>
                        <MenuItem value="name-asc">Nume (A-Z)</MenuItem>
                        <MenuItem value="name-desc">Nume (Z-A)</MenuItem>
                        <MenuItem value="price-asc">Preț (crescător)</MenuItem>
                        <MenuItem value="price-desc">Preț (descrescător)</MenuItem>
                      </TextField>
                    </div>
                    {products.length === 0 ? (
                      <div className="menu-empty-state">Niciun produs.</div>
                    ) : (
                      <DragDropContext onDragEnd={this.onProductDragEnd}>
                        <Droppable droppableId="products">
                          {(droppableProvided) => (
                            <div
                              className="menu-product-list"
                              ref={droppableProvided.innerRef}
                              {...droppableProvided.droppableProps}
                            >
                              {sortedProducts.map((product, index) => (
                                <Draggable
                                  key={product.id}
                                  draggableId={String(product.id)}
                                  index={index}
                                  isDragDisabled={productDragDisabled}
                                >
                                  {(draggableProvided, draggableSnapshot) => (
                                    <div
                                      ref={draggableProvided.innerRef}
                                      {...draggableProvided.draggableProps}
                                      {...draggableProvided.dragHandleProps}
                                      className={`menu-product-row${product.deleted ? ' menu-product-row-deleted' : ''}${draggableSnapshot.isDragging ? ' menu-product-row-dragging' : ''}${product.id === selectedProductId ? ' menu-product-row-selected' : ''}`}
                                      onClick={() => this.selectProduct(product)}
                                    >
                                      <DragHandleIcon
                                        className={`menu-product-drag-handle${productDragDisabled ? ' menu-product-drag-handle-disabled' : ''}`}
                                        fontSize="small"
                                        titleAccess={productDragDisabled ? 'Reordonarea prin tragere e disponibilă doar la „Ordine manuală”' : undefined}
                                      />
                                      <div className="menu-product-image">
                                        {product.image ? (
                                          <img
                                            src={product.image}
                                            alt={product.name}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              this.setState({lightboxSrc: product.image});
                                            }}
                                          />
                                        ) : (
                                          <span className="menu-product-image-placeholder"/>
                                        )}
                                      </div>
                                      <span className="menu-product-name">{product.name}</span>
                                      <div className="menu-product-price-col">
                                        {!!product.old_price && (
                                          <span className="menu-product-old-price">
                                            {(parseFloat(product.old_price) || 0).toFixed(2)} lei
                                          </span>
                                        )}
                                        <span className="menu-product-price">
                                          {(parseFloat(product.price) || 0).toFixed(2)} lei
                                        </span>
                                      </div>
                                      <IconButton
                                        size="small"
                                        title="Editează produs"
                                        className="menu-product-edit-button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          this.openEditProductDialog(product);
                                        }}
                                      >
                                        <EditIcon fontSize="small"/>
                                      </IconButton>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {droppableProvided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="menu-column">
              <div className="menu-column-title-row">
                <div className="menu-column-title">Categorii Extra</div>
                <IconButton
                  size="small"
                  title="Adaugă categorie extra"
                  disabled={!selectedProductId}
                  onClick={this.openAddExtraDialog}
                >
                  <AddIcon fontSize="small"/>
                </IconButton>
              </div>
              <div className="menu-column-body">
                {!selectedProductId ? (
                  <div className="menu-empty-state">Selectează un produs.</div>
                ) : loadingProductExtraCategories ? (
                  <div className="menu-empty-state">Se încarcă...</div>
                ) : productExtraCategories.length === 0 ? (
                  <div className="menu-empty-state">Nicio categorie extra.</div>
                ) : (
                  <DragDropContext onDragEnd={this.onExtraCategoryDragEnd}>
                    <Droppable droppableId="extra-categories">
                      {(droppableProvided) => (
                        <div
                          className="menu-category-list"
                          ref={droppableProvided.innerRef}
                          {...droppableProvided.droppableProps}
                        >
                          {productExtraCategories.map((category, index) => (
                            <Draggable key={category.id} draggableId={`ec-${category.id}`} index={index}>
                              {(draggableProvided, draggableSnapshot) => (
                                <div
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  className={`menu-category-row menu-category-row-editable${category.id === selectedExtraCategoryId ? ' menu-category-row-selected' : ''}${draggableSnapshot.isDragging ? ' menu-product-row-dragging' : ''}`}
                                  onClick={() => this.selectExtraCategory(category.id)}
                                >
                                  <DragHandleIcon className="menu-product-drag-handle" fontSize="small"/>
                                  <div className="menu-category-row-info">
                                    <span className="menu-category-row-name">{category.name}</span>
                                    <span className="menu-category-row-limits">{formatExtraCategoryLimits(category)}</span>
                                  </div>
                                  <IconButton
                                    size="small"
                                    title="Editează categoria extra"
                                    className="menu-category-edit-button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      this.openEditExtraCategoryDialog(category);
                                    }}
                                  >
                                    <EditIcon fontSize="small"/>
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    title="Elimină categoria de la produs"
                                    className="menu-category-edit-button"
                                    onClick={(event) => this.detachExtraCategory(category, event)}
                                  >
                                    <DeleteIcon fontSize="small"/>
                                  </IconButton>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>
            </div>
            <div className="menu-column">
              <div className="menu-column-title-row">
                <div className="menu-column-title">Extra</div>
                <IconButton
                  size="small"
                  title="Adaugă extra"
                  disabled={!selectedExtraCategoryId}
                  onClick={this.openAddExtraItemDialog}
                >
                  <AddIcon fontSize="small"/>
                </IconButton>
              </div>
              <div className="menu-column-body">
                {!selectedExtraCategoryId ? (
                  <div className="menu-empty-state">Selectează o categorie extra.</div>
                ) : loadingCategoryExtras ? (
                  <div className="menu-empty-state">Se încarcă...</div>
                ) : categoryExtras.length === 0 ? (
                  <div className="menu-empty-state">Niciun extra.</div>
                ) : (
                  <DragDropContext onDragEnd={this.onExtraDragEnd}>
                    <Droppable droppableId="extras">
                      {(droppableProvided) => (
                        <div
                          className="menu-product-list"
                          ref={droppableProvided.innerRef}
                          {...droppableProvided.droppableProps}
                        >
                          {categoryExtras.map((extra, index) => (
                            <Draggable key={extra.id} draggableId={`e-${extra.id}`} index={index}>
                              {(draggableProvided, draggableSnapshot) => (
                                <div
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  className={`menu-product-row${draggableSnapshot.isDragging ? ' menu-product-row-dragging' : ''}`}
                                >
                                  <DragHandleIcon className="menu-product-drag-handle" fontSize="small"/>
                                  <span className="menu-product-name">{extra.name}</span>
                                  <span className="menu-product-price">{(parseFloat(extra.price) || 0).toFixed(2)} lei</span>
                                  <IconButton
                                    size="small"
                                    title="Editează extra"
                                    className="menu-product-edit-button"
                                    onClick={() => this.openEditExtraItemDialog(extra)}
                                  >
                                    <EditIcon fontSize="small"/>
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    title="Elimină extra de la produs"
                                    className="menu-product-edit-button"
                                    onClick={() => this.detachExtra(extra)}
                                  >
                                    <DeleteIcon fontSize="small"/>
                                  </IconButton>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>
            </div>
          </div>
        )}

        <Dialog open={categoryDialogOpen} onClose={this.closeCategoryDialog} fullWidth maxWidth="xs">
          <DialogTitle>Adaugă Categorie</DialogTitle>
          <DialogContent>
            <TextField
              select
              label="Categorie existentă"
              value={selectedExistingCategoryId}
              onChange={(event) => this.setState({selectedExistingCategoryId: event.target.value, newCategoryName: ''})}
              margin="dense"
              fullWidth
            >
              <MenuItem value="">Alege categorie</MenuItem>
              {categories
                .filter((category) => !restaurantCategories.some((rc) => rc.id === category.id))
                .map((category) => (
                  <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                ))}
            </TextField>
            <div className="category-dialog-divider">sau</div>
            <TextField
              label="Categorie nouă"
              value={newCategoryName}
              onChange={(event) => this.setState({newCategoryName: event.target.value, selectedExistingCategoryId: ''})}
              margin="dense"
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeCategoryDialog} disabled={categorySaving}>
              Anulează
            </Button>
            <Button
              onClick={this.submitCategoryDialog}
              color="primary"
              variant="contained"
              disabled={categorySaving || (!newCategoryName.trim() && !selectedExistingCategoryId)}
            >
              Adaugă
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={extraCategoryDialogOpen} onClose={this.closeExtraCategoryDialog} fullWidth maxWidth="xs">
          <DialogTitle>Editează Categorie Extra</DialogTitle>
          <DialogContent>
            <TextField
              label="Nume"
              value={extraCategoryForm.name}
              onChange={this.updateExtraCategoryFormField('name')}
              margin="dense"
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={extraCategoryForm.unlimited}
                  onChange={this.toggleExtraCategoryUnlimited}
                  color="primary"
                />
              }
              label="Nelimitat"
            />
            {!extraCategoryForm.unlimited && (
              <>
                <TextField
                  label="Selecții minime"
                  type="number"
                  value={extraCategoryForm.min_selection}
                  onChange={this.updateExtraCategoryFormField('min_selection')}
                  margin="dense"
                  fullWidth
                />
                <TextField
                  label="Selecții maxime"
                  type="number"
                  value={extraCategoryForm.max_selection}
                  onChange={this.updateExtraCategoryFormField('max_selection')}
                  margin="dense"
                  fullWidth
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeExtraCategoryDialog} disabled={extraCategorySaving}>
              Anulează
            </Button>
            <Button
              onClick={this.saveExtraCategory}
              color="primary"
              variant="contained"
              disabled={extraCategorySaving || !extraCategoryForm.name.trim()}
            >
              Salvează
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={addExtraDialogOpen} onClose={this.closeAddExtraDialog} fullWidth maxWidth="xs">
          <DialogTitle>Adaugă Extra</DialogTitle>
          <DialogContent>
            <TextField
              select
              label="Categorie extra existentă"
              value={selectedExistingExtraCategoryId}
              onChange={(event) => {
                // Clicks on the search field above the list don't pick anything.
                if (event.target.value === undefined) {
                  return;
                }
                this.setState({
                  selectedExistingExtraCategoryId: event.target.value,
                  newExtraCategoryName: '',
                });
              }}
              SelectProps={{
                onOpen: () => this.setState({extraCategorySearch: ''}),
                // Keep the focus in the search field instead of the first category.
                MenuProps: {autoFocus: false, disableAutoFocusItem: true, getContentAnchorEl: null,
                  anchorOrigin: {vertical: 'bottom', horizontal: 'left'}},
              }}
              margin="dense"
              fullWidth
            >
              {/* Search on top of the (long) list; typing must not trigger the menu's own type-ahead. */}
              <ListSubheader className="extra-category-search" onKeyDown={(event) => event.stopPropagation()}>
                <TextField
                  autoFocus
                  placeholder="Caută categorie"
                  value={extraCategorySearch}
                  onChange={(event) => this.setState({extraCategorySearch: event.target.value})}
                  onClick={(event) => event.stopPropagation()}
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </ListSubheader>
              <MenuItem value="">Alege categorie</MenuItem>
              {filteredExtraCategories.length === 0 && (
                <MenuItem disabled>Nicio categorie găsită</MenuItem>
              )}
              {filteredExtraCategories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  <div className="extra-category-option">
                    <span className="extra-category-option-name">{category.name}</span>
                    <span className="extra-category-option-limits">{formatExtraCategoryLimits(category)}</span>
                  </div>
                </MenuItem>
              ))}
            </TextField>
            <div className="category-dialog-divider">sau</div>
            <TextField
              label="Categorie extra nouă"
              value={newExtraCategoryName}
              onChange={(event) => this.setState({
                newExtraCategoryName: event.target.value,
                selectedExistingExtraCategoryId: '',
              })}
              margin="dense"
              fullWidth
            />
            {!!newExtraCategoryName.trim() && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newExtraCategoryUnlimited}
                      onChange={this.toggleNewExtraCategoryUnlimited}
                      color="primary"
                    />
                  }
                  label="Nelimitat"
                />
                {!newExtraCategoryUnlimited && (
                  <>
                    <TextField
                      label="Selecții minime"
                      type="number"
                      value={newExtraCategoryMinSelection}
                      onChange={(event) => this.setState({newExtraCategoryMinSelection: event.target.value})}
                      margin="dense"
                      fullWidth
                    />
                    <TextField
                      label="Selecții maxime"
                      type="number"
                      value={newExtraCategoryMaxSelection}
                      onChange={(event) => this.setState({newExtraCategoryMaxSelection: event.target.value})}
                      margin="dense"
                      fullWidth
                    />
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeAddExtraDialog} disabled={addExtraCategorySaving}>
              Anulează
            </Button>
            <Button
              onClick={this.submitAddExtraDialog}
              color="primary"
              variant="contained"
              disabled={addExtraCategorySaving || (!newExtraCategoryName.trim() && !selectedExistingExtraCategoryId)}
            >
              Adaugă
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={addExtraItemDialogOpen} onClose={this.closeAddExtraItemDialog} fullWidth maxWidth="xs">
          <DialogTitle>Adaugă Extra</DialogTitle>
          <DialogContent>
            {restaurantCategoryExtras.length > 0 && (() => {
              const selectableIds = restaurantCategoryExtras
                .filter((extra) => !this.isExtraAlreadyAssigned(extra.id))
                .map((extra) => extra.id);
              const allSelected = selectableIds.length > 0
                && selectableIds.every((id) => selectedExistingExtraIds.includes(id));
              const someSelected = selectableIds.some((id) => selectedExistingExtraIds.includes(id));
              return (
              <table className="extra-select-table">
                <thead>
                <tr>
                  <th>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={!allSelected && someSelected}
                      disabled={selectableIds.length === 0}
                      onChange={this.toggleSelectAllExtras}
                      color="primary"
                      size="small"
                    />
                  </th>
                  <th>Nume</th>
                  <th>Preț</th>
                </tr>
                </thead>
                <tbody>
                {restaurantCategoryExtras.map((extra) => {
                  const alreadyAssigned = this.isExtraAlreadyAssigned(extra.id);
                  const checked = alreadyAssigned || selectedExistingExtraIds.includes(extra.id);
                  return (
                    <tr
                      key={extra.id}
                      className={alreadyAssigned ? 'extra-select-row-disabled' : ''}
                      onClick={() => this.toggleExistingExtraSelection(extra.id)}
                    >
                      <td>
                        <Checkbox
                          checked={checked}
                          disabled={alreadyAssigned}
                          color="primary"
                          size="small"
                        />
                      </td>
                      <td>{extra.name}</td>
                      <td>{(parseFloat(extra.price) || 0).toFixed(2)} lei</td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
              );
            })()}
            <div className="category-dialog-divider">sau</div>
            <TextField
              label="Nume extra nou"
              value={newExtraName}
              onChange={(event) => this.setState({
                newExtraName: event.target.value,
                selectedExistingExtraIds: [],
              })}
              margin="dense"
              fullWidth
            />
            {!!newExtraName.trim() && (
              <>
                <TextField
                  label="Preț"
                  type="number"
                  value={newExtraPrice}
                  onChange={(event) => this.setState({newExtraPrice: event.target.value})}
                  margin="dense"
                  fullWidth
                />
                <TextField
                  label="Descriere (opțional)"
                  value={this.state.newExtraDescription}
                  onChange={(event) => this.setState({newExtraDescription: event.target.value})}
                  inputProps={{maxLength: 500}}
                  margin="dense"
                  multiline
                  fullWidth
                />
                <ExtraPhotoField value={this.state.newExtraImage} onChange={(url) => this.setState({newExtraImage: url})}/>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newExtraAvailable}
                      onChange={this.toggleNewExtraAvailable}
                      color="primary"
                    />
                  }
                  label="Disponibil"
                />
                {hasEbriza && (
                  <>
                    <TextField
                      label="SKU"
                      value={newExtraSku}
                      onChange={(event) => this.setState({newExtraSku: event.target.value})}
                      margin="dense"
                      fullWidth
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newExtraSgr}
                          onChange={this.toggleNewExtraSgr}
                          color="primary"
                        />
                      }
                      label="SGR"
                    />
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeAddExtraItemDialog} disabled={addExtraItemSaving}>
              Anulează
            </Button>
            <Button
              onClick={this.submitAddExtraItemDialog}
              color="primary"
              variant="contained"
              disabled={
                addExtraItemSaving ||
                !selectedExtraCategoryId ||
                (!newExtraName.trim() && !selectedExistingExtraIds.length)
              }
            >
              Adaugă
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editExtraDialogOpen} onClose={this.closeEditExtraItemDialog} fullWidth maxWidth="xs">
          <DialogTitle>Editează Extra</DialogTitle>
          <DialogContent>
            <TextField
              label="Nume"
              value={editExtraForm.name}
              onChange={this.updateEditExtraFormField('name')}
              margin="dense"
              fullWidth
            />
            <TextField
              label="Preț"
              type="number"
              value={editExtraForm.price}
              onChange={this.updateEditExtraFormField('price')}
              margin="dense"
              fullWidth
            />
            <TextField
              label="Descriere (opțional)"
              value={editExtraForm.description}
              onChange={this.updateEditExtraFormField('description')}
              inputProps={{maxLength: 500}}
              margin="dense"
              multiline
              fullWidth
            />
            <ExtraPhotoField
              value={editExtraForm.image}
              onChange={(url) => this.setState({editExtraForm: {...this.state.editExtraForm, image: url}})}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editExtraForm.available}
                  onChange={this.toggleEditExtraAvailable}
                  color="primary"
                />
              }
              label="Disponibil"
            />
            {hasEbriza && (
              <>
                <TextField
                  label="SKU"
                  value={editExtraForm.sku}
                  onChange={this.updateEditExtraFormField('sku')}
                  margin="dense"
                  fullWidth
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editExtraForm.sgr}
                      onChange={this.toggleEditExtraSgr}
                      color="primary"
                    />
                  }
                  label="SGR"
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeEditExtraItemDialog} disabled={editExtraSaving}>
              Anulează
            </Button>
            <Button
              onClick={this.saveExtraItem}
              color="primary"
              variant="contained"
              disabled={editExtraSaving || !editExtraForm.name.trim()}
            >
              Salvează
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={productDialogOpen} onClose={this.closeProductDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editingProductId ? "Editează Produs" : "Adaugă Produs"}</DialogTitle>
          <DialogContent>
            <div className="product-header">
              <div className="product-image-section">
                <div className="product-image-preview">
                  {this.productImagePreview() ? (
                    <img
                      src={this.productImagePreview()}
                      alt="Produs"
                      onClick={() => this.setState({lightboxSrc: this.productImagePreview()})}
                      style={{cursor: 'pointer'}}
                    />
                  ) : (
                    <span className="product-image-placeholder">Fără imagine</span>
                  )}
                </div>
                <label className="product-image-upload-button">
                  <input type="file" accept="image/*" onChange={this.uploadProductImage}/>
                  Încarcă imagine
                </label>
              </div>
              <TextField
                label="Nume"
                value={productForm.name}
                onChange={this.updateProductFormField('name')}
                margin="dense"
                fullWidth
                InputProps={{className: "product-name-input"}}
                InputLabelProps={{className: "product-name-label"}}
              />
            </div>

            <TextField
              label="Descriere"
              value={productForm.description}
              onChange={this.updateProductFormField('description')}
              margin="dense"
              multiline
              rows={4}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={productForm.has_discount}
                  onChange={this.toggleProductHasDiscount}
                  color="primary"
                />
              }
              label="Reducere"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={productForm.recommended}
                  onChange={(event) =>
                    this.setState({productForm: {...this.state.productForm, recommended: event.target.checked}})
                  }
                  color="primary"
                />
              }
              label="Recomandat (apare la Recomandări în checkout-ul web)"
            />

            <div className="product-form-grid">
              {!!editingProductId && (
                <TextField
                  select
                  label="Categorie"
                  value={productForm.category_id}
                  onChange={this.updateProductFormField('category_id')}
                  margin="dense"
                >
                  {restaurantCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                label="Preț"
                type="number"
                value={productForm.price}
                onChange={this.updateProductFormField('price')}
                margin="dense"
              />
              {productForm.has_discount && (
                <TextField
                  label="Preț vechi"
                  type="number"
                  value={productForm.old_price}
                  onChange={this.updateProductFormField('old_price')}
                  margin="dense"
                />
              )}
              <TextField
                label="Taxă ambalaj"
                type="number"
                value={productForm.package_fee}
                onChange={this.updateProductFormField('package_fee')}
                margin="dense"
              />
            </div>

            <FormControlLabel
              control={
                <Switch
                  checked={productForm.available}
                  onChange={this.toggleProductAvailable}
                  color="primary"
                />
              }
              label="Disponibil"
            />

            <div className="product-section-title">Valori Nutriționale</div>
            <FormControlLabel
              control={
                <Switch
                  checked={productForm.ingredients_is_image}
                  onChange={this.toggleProductIngredientsIsImage}
                  color="primary"
                />
              }
              label="Imagine"
            />
            {productForm.ingredients_is_image ? (
              <div className="product-image-section">
                <div className="product-image-preview">
                  {this.productIngredientsImagePreview() ? (
                    <img
                      src={this.productIngredientsImagePreview()}
                      alt="Valori nutriționale"
                      onClick={() => this.setState({lightboxSrc: this.productIngredientsImagePreview()})}
                      style={{cursor: 'pointer'}}
                    />
                  ) : (
                    <span className="product-image-placeholder">Fără imagine</span>
                  )}
                </div>
                <label className="product-image-upload-button">
                  <input type="file" accept="image/*" onChange={this.uploadProductIngredientsImage}/>
                  Încarcă imagine
                </label>
              </div>
            ) : (
              <TextField
                value={productForm.ingredients}
                onChange={this.updateProductFormField('ingredients')}
                margin="dense"
                multiline
                rows={4}
                fullWidth
              />
            )}

            {hasEbriza && (
              <>
                <div className="product-section-title">Ebriza</div>
                <div className="product-form-grid">
                  <TextField
                    label="SKU"
                    value={productForm.sku}
                    onChange={this.updateProductFormField('sku')}
                    margin="dense"
                  />
                </div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={productForm.sgr}
                      onChange={this.toggleProductSgr}
                      color="primary"
                    />
                  }
                  label="SGR"
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            {!!editingProductId && (
              editingProductDeleted ? (
                <Button
                  onClick={this.recoverProduct}
                  disabled={productSaving}
                  startIcon={<RestoreIcon/>}
                  style={{marginRight: 'auto'}}
                >
                  Recuperează
                </Button>
              ) : (
                <Button
                  onClick={this.deleteProduct}
                  disabled={productSaving}
                  startIcon={<DeleteIcon/>}
                  style={{marginRight: 'auto', color: '#d32f2f'}}
                >
                  Șterge
                </Button>
              )
            )}
            <Button onClick={this.closeProductDialog} disabled={productSaving}>
              Anulează
            </Button>
            <Button
              onClick={this.saveProduct}
              color="primary"
              variant="contained"
              disabled={productSaving || !productForm.name || editingProductDeleted}
            >
              Salvează
            </Button>
          </DialogActions>
        </Dialog>

        <ImageLightbox src={lightboxSrc} onClose={() => this.setState({lightboxSrc: null})}/>
      </div>
    );
  }
}
